/**
 * The spritesheet cuts of the media the editor shows (spec 033 D-3). A cut
 * lives on the media item (`Media.sheet`, `GET /media/:id`); the editor
 * fetches it with each media item it loads for a scene and hands the cuts to
 * `toPlayableScene` for the canvas, previews and device preview. Not part of
 * any scene's undo history (D-8): a cut edit is a media edit.
 */

import { create } from 'zustand';
import type { SheetCut, SpriteSize } from '../../../Shared/src/@types';
import { parseSheetCut, sheetFrameSize } from '../../../Shared/src/utils/spriteSheet';
import { apiClient } from '../api/client';
import { registerSessionReset } from '../api/session';

export interface MediaSheetState {
  /** Cuts by media id, of the media loaded so far that have one. */
  sheets: Record<number, SheetCut>;
  /** Media ids whose metadata has been read, with or without a cut. */
  known: Record<number, true>;
  /** The names of the media read so far (spec 035: the States panel names a state's picture). */
  names: Record<number, string>;
  /** Audio and video lengths in ms of the media read so far that have one (spec 041). */
  lengths: Record<number, number>;
  /** Record a media item's length (undefined: unknown), e.g. from the item a picker handed back. */
  setLength: (mediaId: number, durationMs: number | undefined) => void;
  /** Record a media item's cut (undefined: it has none), e.g. after `PATCH /media/:id` or from a media list. */
  setSheet: (mediaId: number, cut: SheetCut | undefined) => void;
  /** Read the cuts of these media ids not read yet; resolves with the cuts known for them. */
  loadSheets: (mediaIds: Iterable<number>) => Promise<Record<number, SheetCut>>;
  reset: () => void;
}

const inFlight = new Map<number, Promise<void>>();

export const useMediaSheetStore = create<MediaSheetState>()((set, get) => ({
  sheets: {},
  known: {},
  names: {},
  lengths: {},

  setLength: (mediaId, durationMs) => {
    set((s) => {
      const lengths = { ...s.lengths };
      if (typeof durationMs === 'number' && Number.isFinite(durationMs) && durationMs > 0) lengths[mediaId] = durationMs;
      else delete lengths[mediaId];
      return { lengths };
    });
  },

  setSheet: (mediaId, cut) => {
    set((s) => {
      const sheets = { ...s.sheets };
      if (cut) sheets[mediaId] = cut;
      else delete sheets[mediaId];
      return { sheets, known: { ...s.known, [mediaId]: true } };
    });
  },

  loadSheets: async (mediaIds) => {
    const ids = [...new Set([...mediaIds].filter((id) => typeof id === 'number' && id > 0))];
    await Promise.all(ids.map((id) => {
      if (get().known[id]) return undefined;
      let p = inFlight.get(id);
      if (!p) {
        p = (async () => {
          try {
            const res = await apiClient.getMediaById(id);
            if (res?.success && res.data) {
              get().setSheet(id, parseSheetCut(res.data.sheet));
              if (typeof res.data.name === 'string') set((s) => ({ names: { ...s.names, [id]: res.data.name } }));
              if (typeof res.data.durationMs === 'number') get().setLength(id, res.data.durationMs);
            }
          } catch {
            // Unreadable metadata: the picture plays as a still.
          } finally {
            inFlight.delete(id);
          }
        })();
        inFlight.set(id, p);
      }
      return p;
    }));
    const { sheets } = get();
    const out: Record<number, SheetCut> = {};
    for (const id of ids) if (sheets[id]) out[id] = sheets[id];
    return out;
  },

  reset: () => {
    inFlight.clear();
    set({ sheets: {}, known: {}, names: {}, lengths: {} });
  },
}));

/** The cuts known now, by media id (what `getPlayableScene` passes to `toPlayableScene`). */
export const knownSheets = (): Record<number, SheetCut> => useMediaSheetStore.getState().sheets;

/** Every picture a scene's sprite states use. */
export function sceneSpriteMediaIds(scene: { sprites?: { states?: { mediaId?: number }[] }[] } | null | undefined): number[] {
  const ids = new Set<number>();
  for (const sprite of scene?.sprites ?? []) {
    for (const state of sprite?.states ?? []) if (typeof state?.mediaId === 'number' && state.mediaId > 0) ids.add(state.mediaId);
  }
  return [...ids];
}

/**
 * The size a picture sizes a sprite by: its frame's size when it has a cut
 * (a sheet never sizes a sprite to the whole sheet), else its natural size.
 */
export function pictureSizeOf(mediaId: number, natural: SpriteSize): SpriteSize {
  return sheetFrameSize(useMediaSheetStore.getState().sheets[mediaId], natural);
}

registerSessionReset(() => useMediaSheetStore.getState().reset());
